import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, LayoutDashboard, Home, Settings as Cog, LogOut, Megaphone, X, Wrench, Users2, Crown } from "lucide-react";

export function StatusDot({ status, className = "" }) {
  const map = { green: "bg-emerald-600", amber: "bg-amber-500", red: "bg-rose-600" };
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${map[status] || "bg-slate-400"} ${className}`} />;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [updates, setUpdates] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/lawupdates").then(({ data }) => setUpdates(data)).catch(() => {});
  }, []);

  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/properties", label: "Properties", icon: Home },
    { to: "/tools", label: "Tools", icon: Wrench },
    { to: "/portfolio", label: "Portfolio", icon: Crown },
    { to: "/shared", label: "Shared", icon: Users2 },
    { to: "/settings", label: "Settings", icon: Cog },
  ];
  if (user?.role === "admin") items.splice(2, 0, { to: "/admin", label: "Admin", icon: ShieldCheck });
  if (user?.is_contractor) items.splice(1, 0, { to: "/jobs", label: "My jobs", icon: Wrench });

  const active = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");
  const latest = updates[0];

  return (
    <div className="min-h-screen bg-background pb-24 sm:pb-0">
      {user?.acting_as && (
        <div data-testid="acting-banner" className="bg-emerald-brand text-white text-sm px-4 py-2 flex items-center justify-center gap-3">
          <span>Managing <b>{user.name || user.email}</b>'s portfolio as {user.actor_email}</span>
          <button data-testid="exit-manage" onClick={() => { localStorage.removeItem("certy_act_as"); window.location.href = "/shared"; }} className="underline font-semibold">Exit</button>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-navy text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" data-testid="brand-logo" className="flex items-center gap-2 font-heading font-extrabold text-lg">
            <img src="/certy-icon.png" alt="GetCerty" className="h-9 w-9 rounded-lg bg-white p-0.5 object-contain" />
            GetCerty
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {items.map((it) => (
              <Link key={it.to} to={it.to} data-testid={`nav-${it.label.toLowerCase()}`}
                className={`px-3 h-10 flex items-center gap-2 rounded-lg text-sm font-medium transition-colors ${active(it.to) ? "bg-white/15" : "hover:bg-white/10 text-white/80"}`}>
                <it.icon size={16} /> {it.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-white/70 max-w-[160px] truncate">{user?.name || user?.email}</span>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
              className="h-10 w-10 grid place-items-center rounded-lg hover:bg-white/10 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      {latest && !dismissed && (
        <div data-testid="law-update-banner" className="bg-amber-100 border-b border-amber-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start gap-3 text-sm text-amber-900">
            <Megaphone size={18} className="mt-0.5 shrink-0" />
            <p className="flex-1"><span className="font-semibold">{latest.nation} law update: </span>{latest.title} — {latest.body}</p>
            <button onClick={() => setDismissed(true)} className="shrink-0"><X size={16} /></button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</main>

      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border flex justify-around items-center no-print">
        {items.map((it) => (
          <Link key={it.to} to={it.to} data-testid={`mobnav-${it.label.toLowerCase()}`}
            className={`flex-1 flex flex-col items-center justify-center gap-1 min-h-[64px] text-xs font-medium ${active(it.to) ? "text-emerald-brand" : "text-muted-foreground"}`}>
            <it.icon size={22} /> {it.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
